import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { PageTitle, Icon } from '@pwc/pwc-deals-ui';
import { Box, Link, useTheme } from '@mui/material';
import { CloseButton, Modal } from 'react-bootstrap';
import {
  getProjectSharePointUrl,
  isDynamicIngestionEnabled,
} from 'features/project/projectSlice';
import { resetPowerPivotFlags } from 'features/analyses/common/LibraryItemCard/PowerPivot/redux/powerPivotSlice';
import featureFlags from 'common/helpers/featureFlags';

export const EmptyClipboardModal = ({ show = true, onHide = () => {} }) => {
  const ref = useRef();
  const theme = useTheme();
  const { t } = useTranslation();

  const enableDynamicIngestionProjectSettings = useSelector(
    isDynamicIngestionEnabled
  );
  const sharepointUrl = useSelector(getProjectSharePointUrl);
  const showSharePointModalPowerPivot = useSelector(
    (state) => state.powerPivot.showSharePointModalPowerPivot
  );
  const dispatch = useDispatch();

  useEffect(() => {
    // Reset Power Pivot flags to default value to have expected behavior next time this component is displayed
    return () => {
      dispatch(resetPowerPivotFlags());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const enableDynamicIngestion =
    enableDynamicIngestionProjectSettings &&
    featureFlags.isWorksheetIngestionEnabled;

  return (
    <Modal
      ref={ref}
      className="object-injest-empty-modal"
      backdrop={false}
      keyboard={false}
      show={show}
    >
      <Modal.Header>
        <Modal.Title>
          <PageTitle title={t('addingAnExcelOrPowerPoint')} size="h2" />
        </Modal.Title>
        <CloseButton onClick={onHide} />
      </Modal.Header>
      <Box>
        <p>
          {enableDynamicIngestion && showSharePointModalPowerPivot ? (
            <>{t('addChartsFromContent')}</>
          ) : (
            <>{t('addChartFromExcel')}</>
          )}
        </p>
        <h4>{t('toAddAnObject')}</h4>
        <Box
          sx={{
            mt: '0.75rem',
            '& li': {
              marginBottom: '0.75rem',
            },
          }}
        >
          <ol>
            <li>
              {t('openDesiredExcel')}{' '}
              <Box
                sx={{ display: 'inline', position: 'relative', pr: '1.5rem' }}
              >
                <Link
                  target="_blank"
                  rel="noreferrer"
                  href={`https://${sharepointUrl}`}
                  sx={{
                    '& polygon': {
                      fill: theme.palette.primary.main,
                    },
                    '& svg': {
                      width: '1rem',
                      height: 'auto',
                    },
                    '&:hover polygon': {
                      fill: theme.palette.action.active,
                    },
                  }}
                >
                  {t('sharePointFolderText')}
                  <Box
                    sx={{
                      display: 'inline',
                      position: 'absolute',
                      marginTop: '-0.1rem',
                      marginLeft: '0.4rem',
                    }}
                  >
                    <Icon
                      name="box-arrow-up-right"
                      width={24}
                      height={24}
                      className="outline icon-orange-theme"
                    />
                  </Box>
                </Link>
              </Box>{' '}
              {t('ribbonKey')}
            </li>
            <li>
              <Box mb="0.75rem">
                <strong>{t('excelKey')}</strong> -{' '}
                {enableDynamicIngestion && showSharePointModalPowerPivot ? (
                  <>{t('selectATableCellRangeOrChart')}</>
                ) : (
                  <>{t('selectTableCellStatic')}</>
                )}
              </Box>
              <Box>
                <strong>{t('powerPoint')}</strong>
                {t('PowerPointText')}
              </Box>
            </li>
            <li>{t('returnJunction')}</li>
          </ol>
        </Box>
      </Box>
    </Modal>
  );
};
